export class CustomAxiosError extends Error {
    constructor(response) {
        // Callers also hand over a bare message (a blob download's reason, a
        // network failure). Read as an object it had no `.message`, so the
        // screen said "Error!" and nothing else.
        if (typeof response === 'string' || response == null) {
            response = { message: response || '' };
        }

        // Ensure to call the parent Error constructor
        super(response.message || 'An error occurred');  // Default message if none is provided

        // Set the name of the error to the class name
        this.name = this.constructor.name;

        // Capture additional error properties
        this.status = response.status || null;  // Status message (e.g., "Unprocessable Content")
        this.code = response.code || null;  // Numeric error code (e.g., 422)
        this.message = response.message || '';  // Error message
        this.data = response.data || null;  // Additional data or null
        this.errors = response.errors || {};  // Validation errors, if any

        // "Validation failed" says that something was wrong, not what. Where
        // the field errors say what, they are the message — so every screen
        // shows the real reason without each having to dig for it.
        const reasons = Object.values(this.errors || {}).flat().filter(Boolean);
        if (reasons.length && (!this.message || /^validation failed\.?$/i.test(this.message.trim()))) {
            this.message = [...new Set(reasons)].slice(0, 3).join(' ');
        }

        // Optional: Capture the stack trace
        if (Error.captureStackTrace) {
            Error.captureStackTrace(this, this.constructor);
        }
    }

    // Helper method to extract specific error messages from the validation errors
    getValidationErrors() {
        const validationErrors = [];
        for (const [field, messages] of Object.entries(this.errors)) {
            validationErrors.push(`${field}: ${messages.join(', ')}`);
        }
        return validationErrors;
    }
}

/**
 * Flattens an API `errors` payload into a list of readable strings.
 *
 * Two shapes arrive here: validation failures ({field: [messages]}) and domain
 * errors ({code, details}). Anything that is not a list of messages is skipped
 * rather than blowing up on `.join`.
 */
export const getValidationErrors = (errors) => {
    const validationErrors = [];

    if (!errors || typeof errors !== "object") return validationErrors;

    for (const [field, messages] of Object.entries(errors)) {
        if (Array.isArray(messages)) {
            validationErrors.push(messages.join(", "));
        } else if (typeof messages === "string" && field !== "code") {
            validationErrors.push(messages);
        }
    }

    return validationErrors;
}
